"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const inputBaseClasses = "flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
function EventInquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground tracking-tight">Inquiry Logged</h3>
        <p className="text-muted-foreground max-w-md mx-auto font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Full Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="e.g. Dr. Sarah Jenkins" className={inputBaseClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="organization" rules={{
        required: "Organization is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Organization</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Institution or Media Outlet" className={inputBaseClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Professional email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid professional email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Email Address</FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="s.jenkins@university.edu.au" className={inputBaseClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" rules={{
        required: "Please select an inquiry type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Inquiry Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full h-10 bg-background rounded-md border-border focus:ring-ring">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="rounded-md">
                  {["Data Licensing", "Media Request", "Methodology Query", "Partnership", "General"].map((type, i) => <SelectItem key={type} value={type} data-index={i}>
                        {type}
                      </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="priority" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Urgency</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full h-10 bg-background rounded-md border-border">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="rounded-md">
                  {["Standard", "Media Deadline", "Urgent Research"].map((type, i) => <SelectItem key={type} value={type} data-index={i}>
                        {type}
                      </SelectItem>)}
                </SelectContent>
              </Select>
              <FormDescription className="text-xs font-default">
                Media requests are prioritized for same-day response.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Contact Number</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="+61 2 0000 0000" className={inputBaseClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold text-foreground">Inquiry Details</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Please provide specific details regarding your data request or media inquiry..." className={`${inputBaseClasses} min-h-[120px] resize-y py-3`} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-default">{submitError}</p>
        </div>}

      <div className="flex justify-end pt-4">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-default font-bold uppercase tracking-wider">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Processing...
            </> : "Submit Formal Inquiry"}
        </Button>
      </div>
    </Form>;
}
export default function EventInquiry() {
  return <section data-section-id="223993" id="reach-out" className="bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/canberra_architecture_minimal.png)] bg-cover bg-center opacity-5 mix-blend-multiply" />
        <div className="absolute inset-0 bg-background/95" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-4xl md:text-6xl font-default font-bold tracking-tighter text-primary">
                  Reach Out
                </h2>
                <p className="text-xl text-muted-foreground font-default leading-relaxed">
                  The Australian Index Bureau maintains an open channel for media, academic researchers, and institutional partners seeking verified economic data.
                </p>
              </div>
            </div>

            <Card className="border-border rounded-lg shadow-md bg-card/50 backdrop-blur-sm">
              <CardHeader className="border-b border-border pb-6 mb-6">
                <CardTitle className="text-2xl font-default font-bold tracking-tight">Formal Inquiry Form</CardTitle>
                <CardDescription className="text-base font-default">
                  Please complete the fields below. All institutional inquiries are logged and tracked for quality assurance.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formKey="reach_out_form_contact" formId="reach_out_form_contact" sectionName="Reach Out" defaultValues={{
                name: "",
                organization: "",
                email: "",
                inquiry_type: "",
                priority: "Standard",
                phone: "",
                message: ""
              }} submitText="Submit Formal Inquiry" successMessage="Your inquiry has been successfully logged in our system. A representative from the relevant department will contact you within one business day.">
                  <EventInquiryFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}